import React, {ClassAttributes, InputHTMLAttributes} from 'react';
import {FieldHookConfig, useField} from "formik";
import FieldHeader from "./FieldHeader";
import FieldHelp from "./FieldHelp";
import {classNames} from "../../../../services/string";

// Component ----

type CustomProps = {
    label?: string
    hint?: string
    help?: string
    maxChars?: number
}

export type TextAreaFieldProps = InputHTMLAttributes<HTMLTextAreaElement>
    & ClassAttributes<HTMLTextAreaElement>
    & FieldHookConfig<string>
    & CustomProps

function TextAreaField({label, hint, help, maxChars, ...props}: TextAreaFieldProps) {
    const [field, meta] = useField(props);

    // Element IDs
    const descriptionId = `${props.name}-description`

    // Input classes
    const textareaBaseClasses = 'block w-full rounded-lg border-0 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:py-1.5 sm:text-sm sm:leading-6'
    const textareaNormalClasses = 'text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-brand'
    const textareaErrorClasses = 'text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500'
    const textareaDisabledClasses = 'text-gray-500 ring-gray-300 placeholder:text-gray-400 cursor-not-allowed'

    // Error state
    const hasError = meta.touched && !!meta.error

    // Disabled state
    const isDisabled = props.disabled;

    // Max chars exceeded state
    const isMaxCharsExceeded = maxChars ? field.value?.length > maxChars : false;

    // Get the textarea styles based on state
    function getTextAreaStyles(): string {
        switch(true) {
            case isDisabled:
                return textareaDisabledClasses;
            case hasError || isMaxCharsExceeded:
                return textareaErrorClasses;
            default:
                return textareaNormalClasses;
        }
    }

    return (
        <div>
            {(!!label || !!hint) && <FieldHeader fieldId={props.id || props.name} label={label} hint={hint} />}

            <div className={classNames((!!label || !!hint) && 'mt-1')}>
                <textarea
                    aria-invalid={hasError}
                    aria-describedby={descriptionId}
                    className={classNames(
                      textareaBaseClasses,
                      getTextAreaStyles(),
                    )}
                    {...field}
                    {...props}>
                </textarea>
            </div>

            <div className='flex justify-between gap-6'>
                {(!!help || hasError) &&
                  <FieldHelp isError={hasError} id={descriptionId}>
                      {hasError ? meta.error : help}
                  </FieldHelp>
                }

                {maxChars &&
                  <span className={classNames(
                    'text-xs text-gray-500 mt-2 ml-auto flex-shrink-0',
                    isMaxCharsExceeded && 'text-red-600')
                  }>
                      {(field.value?.length ?? 0).toLocaleString()} / {maxChars.toLocaleString()}
                  </span>
                }
            </div>


        </div>
    )
}

export default TextAreaField;